/** @jsxImportSource @emotion/react */
import { Button, H5, HTMLTable, Intent, Spinner } from "@blueprintjs/core"
import { IconNames } from "@blueprintjs/icons"
import { format } from "date-fns"
import { ShortLocate } from "../types/common"
import {
  Container,
  EmptyState,
  HeaderContainer,
  LoadingOverlay,
  TableWrapper,
  intentToColor,
} from "./ShortLocatesTable.styled"

interface ShortLocatesTableProps {
  locates: ShortLocate[]
  isLoading: boolean
  onRefresh: () => void
}

const formatDateTime = (dateStr: string) => {
  try {
    return format(new Date(dateStr), "yyyy-MM-dd HH:mm:ss")
  } catch (e) {
    return dateStr
  }
}

const ShortLocatesTable = ({ locates, isLoading, onRefresh }: ShortLocatesTableProps) => {
  const getStatusIntent = (status: string): Intent => {
    if (status.includes("COMPLETE")) return Intent.SUCCESS
    if (status.includes("PENDING") || status.includes("SUBMITTED")) return Intent.PRIMARY
    if (status.includes("PARTIAL")) return Intent.WARNING
    if (
      status.includes("FAILED") ||
      status.includes("REJECTED") ||
      status.includes("CANCELLED") ||
      status.includes("ERROR")
    )
      return Intent.DANGER
    return Intent.NONE
  }

  const renderStatus = (status: string) => {
    const intent = getStatusIntent(status)
    return <span style={{ color: intentToColor[intent] }}>{status.replace("_STATUS", "")}</span>
  }

  return (
    <div>
      <HeaderContainer>
        <H5>Short Locates</H5>
        <Button
          icon={IconNames.REFRESH}
          intent={Intent.PRIMARY}
          small
          onClick={onRefresh}
          loading={isLoading}
        >
          Refresh
        </Button>
      </HeaderContainer>
      <Container>
        {isLoading && (
          <LoadingOverlay>
            <Spinner size={20} />
          </LoadingOverlay>
        )}

        {locates.length === 0 && !isLoading ? (
          <EmptyState>No short locates found. Create one using the form.</EmptyState>
        ) : (
          <TableWrapper style={{ opacity: isLoading ? 0.5 : 1 }}>
            <HTMLTable striped className="bp5-html-table-striped bp5-html-table-condensed">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Broker</th>
                  <th>Quantity</th>
                  <th>Status</th>
                  <th>Time</th>
                  <th>Expiration</th>
                </tr>
              </thead>
              <tbody>
                {locates.map(locate => (
                  <tr key={locate.id}>
                    <td>{locate.symbol.replace(" Equity", "")}</td>
                    <td>{locate.broker}</td>
                    <td>{locate.requested_quantity}</td>
                    <td>{renderStatus(locate.status)}</td>
                    <td>{formatDateTime(locate.time)}</td>
                    <td>
                      {locate.expiration_time ? formatDateTime(locate.expiration_time) : "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </HTMLTable>
          </TableWrapper>
        )}
      </Container>
    </div>
  )
}

export default ShortLocatesTable
